import { type RefObject, useEffect, useState } from 'react'

/**
 * useScrollFadeOut
 * Returns an opacity value [0..1] that linearly decreases as the element's bottom
 * approaches a viewport-aligned threshold, over a given fade distance.
 *
 * Example: opacity = 1 when bottom is >= threshold + fadeDistance; opacity = 0 at threshold.
 *
 * @param ref - element ref to observe
 * @param thresholdPx - viewport px (from top) where fading starts/ends
 * @param fadeDistancePx - px distance over which to interpolate opacity
 *
 * @returns number opacity in [0,1]
 */
export const useScrollFadeOut = (
	ref: RefObject<HTMLElement | null>,
	thresholdPx: number,
	fadeDistancePx: number,
) => {
	const [opacity, setOpacity] = useState(1)
	useEffect(() => {
		const handleScrollOrResize = () => {
			if (!ref.current) return

			const rect = ref.current.getBoundingClientRect()
			const distanceToThresholdPx = rect.bottom - thresholdPx

			if (distanceToThresholdPx <= 0) {
				setOpacity(0)
				return
			}

			if (distanceToThresholdPx >= fadeDistancePx) {
				setOpacity(1)
				return
			}

			setOpacity(distanceToThresholdPx / fadeDistancePx)
		}

		handleScrollOrResize()

		window.addEventListener('scroll', handleScrollOrResize, { passive: true })
		window.addEventListener('resize', handleScrollOrResize)
		return () => {
			window.removeEventListener('scroll', handleScrollOrResize)
			window.removeEventListener('resize', handleScrollOrResize)
		}
	}, [ref, thresholdPx, fadeDistancePx])

	return opacity
}
